import { AbstractGameView } from "@lib/titan2-slingo/dist/game/view/abstract/display/AbstractGameView";
import { ControlConstants } from "@lib/titan2/dist/game/control/constant/ControlConstants";
import { ResizeEvent } from "@lib/titan2/dist/game/renderer/Renderer";
import { Point } from "pixi.js";
import { PoolUtil } from "./PoolUtil";
import { splitIntoTokens } from "./TokenSplitter";
import { TextTokenWrapper } from "./YouCouldWinFactory";
/**
 * Provides image tagged text functionality, creating a new text element with image(s) inside of it.
 * Supports using multiple tokens with multiple different images
 * Can provide a maximum size to bound the width
 * @remark
 * When device is rotated, TitanText/TitanSprite wants to reset to its property position, to prevent this we set the text value again
 * We then use pooling to prevent creating multiple elements and instead recycling elements
 */
export class ImageTaggedText extends AbstractGameView {
    constructor(factory, tokenGenerator, landscapeProperties, portraitProperties) {
        super(factory, landscapeProperties, portraitProperties);
        this.tokenGenerator = tokenGenerator;
        this.landscapeProperties = landscapeProperties;
        this.initialised = false;
        this.textElements = [];
        this.tokenElements = [];
        this.textPool = new PoolUtil(() => TextTokenWrapper.createField(this, "text", this.landscapeProperties.textFieldProperties, this.portraitProperties?.textFieldProperties));
        this.tokens = Array.from(this.tokenGenerator.getKeys());
        //Cannot use handleRotate because the TitanText elements may be destroyed due to the performance optimizations TitanText has used.
        this.registerGame(ResizeEvent.POST_ROTATE, this.handleRegenerate);
        this.initialised = true;
    }
    handleRegenerate() {
        if (!this.initialised)
            return;
        this.setText(this.textMessage, true);
        this.renderable = true;
    }
    //Titan text and Titan Sprite will reset position on rotation, we cant access the events from this class to disable that. 
    handleRotate(event) {
        super.handleRotate(event);
        if (!this.initialised)
            return;
        this.renderable = false; //We disable because the text will not be correct until POST_ROTATE is called.
    }
    getText() {
        return this.textMessage;
    }
    reset() {
        this.textElements.forEach(x => this.textPool.releaseElement(x));
        this.tokenElements.forEach(x => this.tokenGenerator.releaseElement(x));
        this.textElements = [];
        this.tokenElements = [];
    }
    async setText(text, forceRegenerate) {
        if (this.textMessage === text && !forceRegenerate)
            return; //no need to re-generate the text
        this.reset();
        this.renderable = false;
        this.textMessage = text;
        const pieces = [];
        const messagePieces = splitIntoTokens(text, this.tokens);
        //Create the text and images
        for (let index = 0; index < messagePieces.length; index++) {
            let workingMessage = messagePieces[index];
            if (this.uiProperties.trimWhiteSpace) {
                workingMessage = workingMessage.trim();
            }
            if (!this.tokens.includes(workingMessage)) {
                //Normal text
                const createdText = this.textPool.getNextElement();
                this.addChild(createdText);
                createdText.setText(workingMessage);
                pieces.push(createdText);
                this.textElements.push(createdText);
            }
            else {
                //Image token
                const createdSprite = this.tokenGenerator.getNextElement(workingMessage);
                this.addChild(createdSprite);
                pieces.push(createdSprite);
                this.tokenElements.push(createdSprite);
            }
        }
        let totalWidth = 0;
        for (const piece of pieces) {
            piece.scale.set(piece.getProperties().scale.x);
            totalWidth += piece.getWidth();
        }
        let x = 0;
        const size = this.uiProperties.size;
        const scaleFactor = size ? Math.min(size.x / totalWidth, 1) : 1;
        for (const piece of pieces) {
            piece.applyPositionAndAnchor(new Point(x, 0));
            piece.scale.set(scaleFactor * piece.getProperties().scale.x);
            x += piece.getWidth();
        }
        if (this.uiProperties.alignment !== ControlConstants.LEFT) {
            this.pivot.x = x / 2;
        }
        this.renderable = true;
    }
}
//# sourceMappingURL=ImageTaggedText.js.map